'use client';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';
import { bgLightBlue, bgPrimary } from '@osmosis/tailwind-ui/styles/background';
import { text0, text3, text4, text5 } from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  TrialCardArgs,
  TrialCtaArgs,
} from '../../lib/composer/schemas/answersValueProp';
import { getFontClass } from '../../lib/composer/schemas/font';
import { track } from '../../lib/util/tracking';
import TrialCTA from '../TrialCTA';

const DEFAULT_SOURCE_CTA = 'answers-trial-card';

/**
 * Where the card lives relative to the answers grid layout switch. When set,
 * visibility follows the content-container width (via `useContentSize`) so it
 * stays in sync with `AnswersGrid`, which moves `AnswersUpperRight` into the
 * right column at the same `xl` container breakpoint (`block xl:grid`).
 * `stacked` is the header placement, shown while stacked (below `xl`) and hidden
 * once the sidebar splits into its own column. `sidebar` is the
 * `AnswersUpperRight` placement, hidden while stacked and shown once the sidebar
 * splits out (at/above `xl`).
 */
type Placement = 'stacked' | 'sidebar';

type Props = {
  /** Editable copy from the composer config; falls back to defaults below. */
  config?: TrialCardArgs;
  /** Shared button/reassurance copy for every Answer-page trial CTA. */
  trialCta?: TrialCtaArgs;
  /** Renders even when a user is logged in (composer preview only). */
  previewMode?: boolean;
  /** Analytics source for the CTA button. */
  sourceCta?: string;
  /** Additional root classes for alternate placements such as landing heroes. */
  className?: string;
  /**
   * Toggles visibility based on the content-container width, keyed to the
   * answers grid layout switch. Omit for non-answers placements such as landing
   * heroes.
   */
  placement?: Placement;
};

const FEATURES = [
  <FormattedMessage
    key="videos"
    id="answers.trialCard.feature.videos"
    defaultMessage="1,700+ whiteboard-style videos"
  />,
  <FormattedMessage
    key="questions"
    id="answers.trialCard.feature.questions"
    defaultMessage="2,700+ board-style questions"
  />,
  <FormattedMessage
    key="flashcards"
    id="answers.trialCard.feature.flashcards"
    defaultMessage="Spaced-repetition flashcards"
  />,
  <FormattedMessage
    key="mobile"
    id="answers.trialCard.feature.mobile"
    defaultMessage="Mobile app — study anywhere"
  />,
];

/**
 * "Go deeper with Osmosis" sidebar trial card shown above the related topics
 * to logged-out users on answer pages.
 */
const AnswerTrialCard = ({
  config,
  trialCta,
  previewMode,
  sourceCta = DEFAULT_SOURCE_CTA,
  className,
  placement,
}: Props) => {
  const { user } = useUserContext();
  const { getClassValue } = useContentSize();

  if (user && !previewMode) return null;

  const placementVisibility = placement
    ? placement === 'stacked'
      ? getClassValue({
          defaultClasses: 'block xl:hidden',
          breakpoints: { xl: 'hidden', base: 'block' },
        })
      : getClassValue({
          defaultClasses: 'hidden xl:block',
          breakpoints: { xl: 'block', base: 'hidden' },
        })
    : undefined;

  const handleClick = () => {
    track('answers-cta-clicked', { source_cta: sourceCta });
  };

  const features = config?.features?.length
    ? config.features.map((feature, index) => ({
        key: `config-${index}`,
        node: feature.text,
      }))
    : FEATURES.map((feature) => ({
        key: feature.key as string,
        node: feature,
      }));

  const fontClass = getFontClass(config?.fontStyle);

  return (
    <div
      data-testid="answer-trial-card"
      className={cn(
        `mb-4 rounded-lg border border-blue-600 p-5 dark:border-blue-400 ${bgLightBlue}`,
        placementVisibility,
        className,
      )}
    >
      <div
        className={cn(
          `mb-3 inline-block rounded px-2 py-1 text-xs font-bold ${bgPrimary} ${text0}`,
          config?.badgeFontSize,
          fontClass,
        )}
        data-testid="answer-trial-card-badge"
      >
        {config?.badge ?? (
          <FormattedMessage
            id="answers.trialCard.badge"
            defaultMessage="7-day free trial"
          />
        )}
      </div>

      <p
        className={cn(
          `mb-2 text-base font-bold ${text4}`,
          config?.headingFontSize,
          fontClass,
        )}
      >
        {config?.heading ?? (
          <FormattedMessage
            id="answers.trialCard.heading"
            defaultMessage="Go deeper with Osmosis"
          />
        )}
      </p>

      <p
        className={cn(
          `mb-3 text-sm leading-none ${text3}`,
          config?.descriptionFontSize,
          fontClass,
        )}
      >
        {config?.description ?? (
          <FormattedMessage
            id="answers.trialCard.description"
            defaultMessage="Videos, flashcards & practice questions built for healthcare learners."
          />
        )}
      </p>

      <div
        className={cn(
          'mb-3 flex items-center gap-1 text-sm font-bold text-ratingGold',
          config?.ratingTextFontSize,
          fontClass,
        )}
        data-testid="answer-trial-card-rating"
      >
        {Array.from({ length: 5 }).map((_, index) => (
          <Icon key={index} icon={faStar} size={14} />
        ))}
        <span className="ml-1 font-normal">
          {config?.ratingText ?? (
            <FormattedMessage
              id="answers.trialCard.rating"
              defaultMessage="4.8 · 12,000+ reviews"
            />
          )}
        </span>
      </div>

      <div className="mb-4 flex flex-col gap-2">
        {features.map((feature) => (
          <div
            key={feature.key}
            className={cn(
              `flex items-center gap-2 text-xs p-0.5 ${text4}`,
              config?.featuresFontSize,
              fontClass,
            )}
          >
            <Icon icon={faCheck} size={14} className={text5} />
            {feature.node}
          </div>
        ))}
      </div>

      <TrialCTA
        copy={trialCta}
        onButtonClick={handleClick}
        buttonTestId="answer-trial-card-button"
        fineprintTestId="answer-trial-card-fineprint"
        containerTestId="answer-trial-card-cta"
        classNames={{
          container: 'w-full rounded-lg',
          button: 'w-full justify-center',
        }}
      />
    </div>
  );
};

export default AnswerTrialCard;
